import { getFileExceededError, getLocalFiles } from '@frontend/forms';
import { useSecureNoteInitialQuestion, type ExtractTaskInitialQuestionWithAnswer } from '@frontend/react-domain';
import type { ButtonProps } from '@frontend/web-react';
import {
  Button,
  DragAndDropModal,
  FileInput,
  Icon,
  PasswordInput,
  Text,
  TextInput,
  TextareaInput,
  XStack,
  YStack,
  showToast,
} from '@frontend/web-react';
import { WebTypedFormData } from '@frontend/web-utils';
import { PrivacyPolicyBanner } from '@frontend/web/components/DomainSpecific/PrivacyPolicyBanner';
import {
  useCreateSecureNoteMutation,
  useLazyRetrieveSecureNoteContentQuery,
  useReplaceSecureNoteContentMutation,
  useRetrieveClientQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import React, { useEffect } from 'react';

type SecureNoteIQProps = {
  initialQuestionWithAnswer: ExtractTaskInitialQuestionWithAnswer<'secure_note'>;
  isLoading: boolean;
  onChange: (iq: ExtractTaskInitialQuestionWithAnswer<'secure_note'>) => void;
  onSuccess: (iq?: ExtractTaskInitialQuestionWithAnswer<'secure_note'>) => Promise<void>;
  setNextButtonComponentOverride: SetStateFn<MaybeUndefined<(props: ButtonProps) => React.ReactNode>>;
};

export const SecureNoteIQ = ({
  initialQuestionWithAnswer,
  isLoading,
  onChange,
  onSuccess,
  setNextButtonComponentOverride,
}: SecureNoteIQProps) => {
  const {
    errors,
    isCreateSecureNoteLoading,
    isRetrieveSecureNoteContentLoading,
    isUpdateSecureNoteContentLoading,
    isValid,
    setFieldValue,
    submitForm,
    values,
  } = useSecureNoteInitialQuestion({
    formDataClass: WebTypedFormData,
    handleMutation,
    initialQuestionWithAnswer,
    onChange,
    onSuccess,
    useCreateSecureNoteMutation,
    useLazyRetrieveSecureNoteContentQuery,
    useReplaceSecureNoteContentMutation,
    useRetrieveClientQuery,
  });

  useEffect(() => {
    setNextButtonComponentOverride(() => {
      return ({ disabled, isLoading, ...props }: ButtonProps) => {
        return (
          <Button
            {...props}
            disabled={disabled || !isValid || isRetrieveSecureNoteContentLoading}
            isLoading={isLoading || isUpdateSecureNoteContentLoading || isCreateSecureNoteLoading}
            onClick={() => {
              submitForm();
            }}
            variant="primary"
          />
        );
      };
    });
  }, [
    isValid,
    submitForm,
    isUpdateSecureNoteContentLoading,
    isCreateSecureNoteLoading,
    isRetrieveSecureNoteContentLoading,
  ]);

  return (
    <YStack className="gap-y-md p-md">
      <TextInput
        autoCapitalize="none"
        defaultValue={values.username}
        disabled={isLoading}
        error={errors?.username}
        label="Email or username"
        onChange={(username) => setFieldValue('username', username)}
        placeholder="Email or username"
        value={values.username}
      />
      <PasswordInput
        disabled={isLoading}
        error={errors?.password}
        label="Password"
        onChange={(password) => setFieldValue('password', password)}
        value={values.password}
      />
      <TextInput
        autoCapitalize="none"
        disabled={isLoading}
        error={errors?.website}
        label="Website"
        onChange={(website) => setFieldValue('website', website)}
        placeholder="getduckbill.com"
        type="url"
        value={values.website || ''}
      />
      <FileInput
        allowedFileTypes={['image', 'pdf']}
        disabled={isLoading}
        label="Attachments"
        onChange={(files) => {
          const localFiles = getLocalFiles(files);
          const fileExceededError = getFileExceededError(localFiles);
          if (fileExceededError) {
            showToast({ title: fileExceededError, variant: 'danger' });
          } else {
            setFieldValue('attachments', files);
          }
        }}
        previewType="listItems"
        showPreview
        value={values.attachments}
      />
      <DragAndDropModal
        accept={{ IMAGE_MIME_TYPE: [] }}
        onFilesDropped={(attachments) => {
          const fileExceededError = getFileExceededError(attachments);
          if (fileExceededError) {
            showToast({ title: fileExceededError, variant: 'danger' });
          } else {
            setFieldValue('attachments', [...values.attachments, ...attachments]);
          }
        }}
      />
      <TextareaInput
        disabled={isLoading}
        label="Notes"
        onChange={(notes) => setFieldValue('notes', notes)}
        placeholder="Anything else we should know?"
        showOptionalText
        value={values.notes}
      />
      <XStack justifyContent="center">
        <PrivacyPolicyBanner />
      </XStack>
      <XStack gapX="xs">
        <Icon color="tertiary" name="IconLock" size={32} />
        <YStack className="shrink">
          <Text color="tertiary" type="p-sm">
            We keep your sensitive info safe and sound, using top-notch encryption and strict access rules – only our
            trained copilots get a peek.
          </Text>
        </YStack>
      </XStack>
    </YStack>
  );
};
